// TEAM_005: Payout status (socket primary + REST polling fallback).
// Supports winner reward, cancel-refund, and manual-payout overpayment-refund flows.

import { useEffect, useMemo, useRef, useState } from 'react';

import type { TournamentPrizePayout, TournamentPrizePayoutStatus } from '../types/tournamentPrizePayout';
import { getTournamentPrizePayoutForMe } from '../api/tournamentPrizePayoutService';

export type TournamentPrizePayoutViewState =
  | 'NOT_WINNER'
  | 'NOT_ELIGIBLE'
  | 'QUEUED'
  | 'NO_PAYOUT'
  | TournamentPrizePayoutStatus;

const IN_PROGRESS_STATUSES: TournamentPrizePayoutStatus[] = ['PENDING', 'INITIATED'];

export const useTournamentPrizePayout = (params: {
  tournamentId: string | number;
  isWinner: boolean;
  socket: any;
  enabled?: boolean;
  pollingIntervalMs?: number;
  isCancelRefundEligible?: boolean;
  /** Whether a reward payout is expected. When false, 404 maps to NO_PAYOUT instead of QUEUED. Default: true. */
  expectsReward?: boolean;
  /** Override for fetch gating. Default: isWinner || isCancelRefundEligible. */
  lookupEnabled?: boolean;
}) => {
  const {
    tournamentId,
    isWinner,
    socket,
    enabled = true,
    pollingIntervalMs = 15000,
    isCancelRefundEligible = false,
    expectsReward = true,
    lookupEnabled: lookupEnabledOverride,
  } = params;

  const defaultLookupEnabled = isWinner || isCancelRefundEligible;
  const lookupEnabled = lookupEnabledOverride ?? defaultLookupEnabled;

  const [payout, setPayout] = useState<TournamentPrizePayout | null>(null);
  const [isQueued, setIsQueued] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const tournamentIdRef = useRef<string>(String(tournamentId));

  useEffect(() => {
    tournamentIdRef.current = String(tournamentId);
  }, [tournamentId]);

  const fetchPayout = useMemo(() => {
    return async () => {
      if (!enabled || !lookupEnabled) {
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getTournamentPrizePayoutForMe(tournamentId);
        setPayout(response.data);
        setIsQueued(false);
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 404) {
          setPayout(null);
          setIsQueued(true);
        } else {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        setIsLoading(false);
      }
    };
  }, [enabled, lookupEnabled, tournamentId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!lookupEnabled) {
      setPayout(null);
      setIsQueued(false);
      setIsLoading(false);
      setError(null);
      return;
    }

    fetchPayout();
  }, [enabled, lookupEnabled, fetchPayout]);

  useEffect(() => {
    if (!enabled || !lookupEnabled) {
      return;
    }

    if (!socket) {
      return;
    }

    const handlePayoutUpdated = (payload: any) => {
      try {
        const next = payload?.tournamentPrizePayout;
        if (!next || typeof next.tournament === 'undefined') {
          return;
        }

        if (String(next.tournament) !== tournamentIdRef.current) {
          return;
        }

        setPayout(next as TournamentPrizePayout);
        setIsQueued(false);
      } catch (err) {
        // Swallow event parsing issues; REST polling acts as fallback.
      }
    };

    socket.on('tournamentPrizePayoutUpdated', handlePayoutUpdated);

    return () => {
      socket.off('tournamentPrizePayoutUpdated', handlePayoutUpdated);
    };
  }, [enabled, lookupEnabled, socket]);

  const shouldPoll = useMemo(() => {
    if (!enabled || !lookupEnabled) {
      return false;
    }

    if (!socket?.connected) {
      return true;
    }

    if (isQueued && expectsReward) {
      return true;
    }

    if (!payout?.status) {
      return true;
    }

    return IN_PROGRESS_STATUSES.includes(payout.status);
  }, [enabled, lookupEnabled, socket?.connected, isQueued, payout?.status]);

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    const timer = setInterval(() => {
      fetchPayout();
    }, pollingIntervalMs);

    return () => clearInterval(timer);
  }, [shouldPoll, fetchPayout, pollingIntervalMs]);

  const viewState: TournamentPrizePayoutViewState = useMemo(() => {
    if (!lookupEnabled) {
      return isCancelRefundEligible ? 'NOT_ELIGIBLE' : 'NOT_WINNER';
    }

    if (payout?.status) {
      return payout.status;
    }

    if (isQueued) {
      return expectsReward ? 'QUEUED' : 'NO_PAYOUT';
    }

    return expectsReward ? 'QUEUED' : 'NO_PAYOUT';
  }, [lookupEnabled, isCancelRefundEligible, payout?.status, isQueued, expectsReward]);

  return {
    payout,
    viewState,
    isLoading,
    error,
    refetch: fetchPayout,
  };
};
